import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Card, CardHeader, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Search, Filter, Calendar, Tag, ArrowLeft, ChevronRight, Share2, User, CheckCircle, AlertCircle } from "lucide-react";
import { useNavigationStore } from "@/stores/navigation";
import { BLOG_POSTS } from "@/data/blogPosts";
import { emailService } from "@/services/emailService";

const POSTS = BLOG_POSTS;


function formatDate(iso: string) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

// LIGHT BLUE LOOK & FEEL HELPERS
const liteBlue = {
  bgBand: "bg-[#EAF3FB]",
  grad: "bg-gradient-to-br from-[#EAF3FB] via-white to-[#D9ECFF]",
  badge: "bg-[#E6F0FA] text-[#0B5BD3]",
  border: "border-[#CBE2FF]",
};

// Product theming: gradient only
function productStyle(primaryTag: string = "AI") {
  switch (primaryTag) {
    case "ReferralCX":
      return { grad: "from-[#0054A6]/20 via-[#7AC043]/10 to-white" };
    case "CellCX":
      return { grad: "from-[#C1E1C5] via-white to-[#A7D7B5]" };
    case "ProbationCX":
      return { grad: "from-[#EAF3FB] via-white to-[#D9ECFF]" };
    case "VaxManagerCX":
      return { grad: "from-[#BAE6FD] via-white to-[#7DD3FC]" };
    case "SchedulingCX":
      return { grad: "from-[#7AC043]/20 via-[#EAF7EE] to-[#D9ECFF]" };
    case "CaseCX":
      return { grad: "from-[#E8D5FF] via-white to-[#D4B3FF]" };
    case "EceptionistCX":
    case "EHR Strategy":
    case "Platform Architecture":
      return { grad: "from-[#0054A6]/20 via-[#7AC043]/10 to-white" };
    case "Operational AI":
      return { grad: "from-[#D0E4FF] via-white to-[#BFD7FF]" };
    default: // AI
      return { grad: "from-[#D0E4FF] via-white to-[#BFD7FF]" };
  }
}

function Hero({ onSearch }: { onSearch?: (q: string) => void }) {
  return (
    <div className={`${liteBlue.grad} border-b`}>
      <div className="mx-auto max-w-6xl px-4 py-12">
        <p className="text-xs uppercase tracking-widest text-[#0B5BD3]">Insights & Perspectives</p>
        <h1 className="mt-2 text-3xl font-bold leading-tight md:text-4xl">Blog — Digital Operations for Healthcare, Justice & Public Health</h1>
        <p className="mt-3 max-w-2xl text-slate-700">Practical guides, case stories, and product thinking from the EceptionistCX team. No fluff—just what works in the real world.</p>
        <div className="mt-5 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
            <Input onChange={(e)=> onSearch?.(e.target.value)} placeholder="Search articles…" className="pl-9" />
          </div>
          <Button variant="outline" className="gap-2"><Filter className="h-4 w-4"/> Filters</Button>
        </div>
      </div>
    </div>
  );
}

function Filters({ categories, active, setActive, sort, setSort }: { categories: string[]; active: string; setActive: (v: string)=>void; sort: string; setSort: (v: string)=>void; }) {
  return (
    <div className="mx-auto max-w-6xl px-4">
      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => (
            <Badge
              key={c}
              onClick={() => setActive(c === active ? "All" : c)}
              className={`cursor-pointer px-3 py-1 ${active === c ? "bg-[#0B5BD3] text-white" : liteBlue.badge} transition-colors`}
            >
              {c}
            </Badge>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Sort by</span>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className={`w-40 border ${liteBlue.border} bg-white`}>
              <SelectValue placeholder="Latest" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="latest">Latest</SelectItem>
              <SelectItem value="oldest">Oldest</SelectItem>
              <SelectItem value="read">Read time</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

function CardArt({ tag }: { tag: string }) {
  const { grad } = productStyle(tag);
  return (
    <div className={`relative h-40 w-full bg-gradient-to-br ${grad}`}>
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 30%, #0B5BD3 2px, transparent 2px), radial-gradient(circle at 80% 70%, #7AC043 2px, transparent 2px)",
          backgroundSize: "60px 60px",
        }}
      />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="flex h-24 w-24 items-center justify-center rounded-full bg-white/80 text-2xl font-bold text-[#0B5BD3] shadow">
          CX
        </div>
      </div>
    </div>
  );
}

function BlogCard({ post, onOpen }: { post: any; onOpen: (p: any)=>void }) {
  const primary = post.tags[0] || "AI";
  return (
    <Card className={`group overflow-hidden rounded-2xl ${liteBlue.border} border hover:shadow-md transition-shadow h-full flex flex-col`}>
      <CardHeader className="p-0">
        <CardArt tag={primary} />
      </CardHeader>
      <CardContent className="space-y-2 p-4">
        <p className="text-xs font-medium uppercase tracking-widest text-[#0B5BD3]">{post.kicker}</p>
        <h3 className="text-lg font-semibold leading-snug">{post.title}</h3>
        <p className="line-clamp-2 text-sm text-slate-600">{post.excerpt}</p>
        <div className="flex flex-wrap gap-2 pt-1">
          {post.tags.map((t: string) => (
            <Badge key={t} variant="secondary" className={`${liteBlue.badge} text-xs`}>{t}</Badge>
          ))}
        </div>
      </CardContent>
      <CardFooter className="mt-auto w-full p-4 text-xs text-slate-500">
        <div className="flex flex-col gap-1 w-full">
          <div className="flex items-center gap-2"><User className="h-3.5 w-3.5"/><span>{post.author}</span></div>
          <div className="flex items-center justify-between gap-2 w-full">
            <div className="flex items-center gap-2"><Calendar className="h-3.5 w-3.5"/><span>{formatDate(post.date)}</span></div>
            <Button size="sm" className="gap-1 bg-black text-white hover:bg-black/90" onClick={() => onOpen(post)}>
              Read <ChevronRight className="h-4 w-4"/>
            </Button>
          </div>
        </div>
      </CardFooter>
    </Card>
  );
}

function Sidebar() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubscribe = async () => {
    if (!email.trim()) {
      setErrorMessage('Please enter your email address');
      setSubscriptionStatus('error');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrorMessage('Please enter a valid email address');
      setSubscriptionStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubscriptionStatus('idle');
    setErrorMessage('');

    try {
      const result = await emailService.sendNewsletterSignup(email);
      
      if (result.success) {
        setSubscriptionStatus('success');
        setEmail(''); // Clear the input on success
      } else {
        setSubscriptionStatus('error');
        setErrorMessage(result.error || 'Failed to subscribe. Please try again.');
      }
    } catch (error) {
      setSubscriptionStatus('error');
      setErrorMessage('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubscribe();
    }
  };

  return (
    <div className="sticky top-20 space-y-6">
      <Card className={`rounded-2xl ${liteBlue.border} border`}>
        <CardContent className="space-y-3 p-4">
          <p className="text-sm font-semibold">Get new posts via email</p>
          
          {subscriptionStatus === 'success' ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-green-600 text-sm">
                <CheckCircle className="h-4 w-4" />
                <span>Successfully subscribed!</span>
              </div>
              <p className="text-xs text-slate-600">
                You'll receive our latest blog posts and updates via email.
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full"
                onClick={() => setSubscriptionStatus('idle')}
              >
                Subscribe Another Email
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <Input 
                placeholder="Your email" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isSubmitting}
                className={subscriptionStatus === 'error' ? 'border-red-500' : ''}
              />
              
              {subscriptionStatus === 'error' && (
                <div className="flex items-center gap-2 text-red-600 text-xs">
                  <AlertCircle className="h-3 w-3" />
                  <span>{errorMessage}</span>
                </div>
              )}
              
              <Button 
                className="w-full bg-black text-white hover:bg-black/90" 
                onClick={handleSubscribe}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


function FeaturedHero({ post, onOpen }: { post: any; onOpen: (p: any) => void }) {
  const primary = post.tags[0] || "AI";
  const { grad } = productStyle(primary);
  
  return (
    <div className={`${liteBlue.bgBand} border-b`}>
      <div className="mx-auto max-w-6xl px-4 py-8">
        <p className="text-xs uppercase tracking-widest text-[#0B5BD3] mb-3">Featured</p>
        <Card className={`overflow-hidden rounded-2xl ${liteBlue.border} border hover:shadow-lg transition-shadow`}>
          <div className="grid md:grid-cols-[2fr_3fr]">
            <div className={`relative h-64 md:h-auto bg-gradient-to-br ${grad}`}>
              <div
                className="absolute inset-0 opacity-20"
                style={{
                  backgroundImage:
                    "radial-gradient(circle at 20% 30%, #0B5BD3 2px, transparent 2px), radial-gradient(circle at 80% 70%, #7AC043 2px, transparent 2px)",
                  backgroundSize: "60px 60px",
                }}
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="flex h-32 w-32 items-center justify-center rounded-full bg-white/80 text-3xl font-bold text-[#0B5BD3] shadow-lg">
                  CX
                </div>
              </div>
            </div>
            <CardContent className="p-8 flex flex-col justify-center space-y-4">
              <Badge className={`${liteBlue.badge} w-fit text-xs`}>{post.kicker}</Badge>
              <h2 className="text-2xl font-bold leading-tight">{post.title}</h2>
              <p className="text-slate-600 leading-relaxed">{post.excerpt}</p>
              <div className="flex items-center gap-4 text-xs text-slate-500 pt-2">
                <span className="inline-flex items-center gap-1"><User className="h-3.5 w-3.5"/>{post.author}</span>
                <span>•</span>
                <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5"/>{formatDate(post.date)}</span>
                <span>•</span>
                <span>{post.read} min read</span>
              </div>
              <div className="pt-2">
                <Button className="bg-black text-white hover:bg-black/90 gap-2" onClick={() => onOpen(post)}>
                  Read Article <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </div>
        </Card>
      </div>
    </div>
  );
}

function DetailPage({ post, onBack, onOpenPost }: { post: any; onBack: ()=>void; onOpenPost: (p: any)=>void }) {
  const navigation = useNavigationStore();
  const primary = post.tags[0] || "AI";
  const { grad } = productStyle(primary);
  
  const handleShare = async () => {
    const postUrl = `${window.location.origin}/blog/${post.id}`;
    const shareData = {
      title: post.title,
      text: post.excerpt || post.subtitle,
      url: postUrl,
    };

    // Try Web Share API first (works on mobile and some desktop browsers)
    if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
        return;
      } catch (error) {
        // User cancelled or error occurred, fall back to clipboard
        console.log('Web Share API failed, falling back to clipboard');
      }
    }

    // Fallback: Copy URL to clipboard
    try {
      await navigator.clipboard.writeText(postUrl);
      // You could add a toast notification here if you have a toast system
      alert('Link copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      // Final fallback: show the URL in a prompt
      prompt('Copy this link to share:', postUrl);
    }
  };
  const cta = useMemo(() => {
    // Check if post has both ProbationCX and CaseCX tags
    const hasProbationCX = post.tags?.includes("ProbationCX");
    const hasCaseCX = post.tags?.includes("CaseCX");
    
    if (hasProbationCX && hasCaseCX) {
      return {
        title: "See ProbationCX and CaseCX in action",
        body: "Explore how digital bridges between justice and healthcare systems improve coordination, compliance, and outcomes.",
      };
    }
    
    switch (primary) {
      case "ReferralCX":
        return {
          title: "See ReferralCX in action",
          body: "Unify internal and external referrals with capacity‑aware scheduling and guided triage.",
        };
      case "AI":
      case "AI in CX":
      case "Operational AI":
        return {
          title: "See AI in EceptionistCX in action",
          body: "Move beyond pilots and deploy AI that actually runs your operations.",
        };
      case "EHR Strategy":
      case "Platform Architecture":
        return {
          title: "See EceptionistCX in action",
          body: "Close operational gaps today—without replacing your EHR.",
        };
      case "CellCX":
        return {
          title: "See CellCX in action",
          body: "Purpose‑built workflows for cell therapy—milestones and cross‑team orchestration.",
        };
      case "ProbationCX":
        return {
          title: "See ProbationCX in action",
          body: "Digitize referrals, automate case workflows, and improve compliance with real‑time dashboards.",
        };
      case "VaxManagerCX":
        return {
          title: "See VaxManagerCX in action",
          body: "Scalable vaccine and clinic management—scheduling, wait lists, forms, and reporting in one platform.",
        };
      case "SchedulingCX":
        return {
          title: "See SchedulingCX in action",
          body: "Centralized scheduling, load balancing, and waitlist management—built for real‑world healthcare operations.",
        };
      case "CaseCX":
        return {
          title: "See CaseCX in action",
          body: "Centralize case coordination, tasks, and communication across multi-party workflows for better outcomes.",
        };
      case "EceptionistCX":
        return {
          title: "See EceptionistCX in action",
          body: "Discover how modular digital architecture accelerates transformation without disruption.",
        };
      default:
        return {
          title: "Ready to modernize your operations?",
          body: "See how EceptionistCX unifies referrals, scheduling, and complex workflows.",
        };
    }
  }, [primary, post.tags]);
  const relatedPosts = useMemo(() => {
    const currentTags: string[] = post.tags || [];
    const candidates = BLOG_POSTS.filter(p => p.id !== post.id);
    const scored = candidates.map(p => {
      const overlap = (p.tags || []).filter((t: string) => currentTags.includes(t)).length;
      return { p, score: overlap };
    });
    const sorted = scored
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score; // more tag overlap first
        return new Date(b.p.date as any as string).getTime() - new Date(a.p.date as any as string).getTime(); // newer first
      })
      .map(s => s.p)
      .slice(0, 3);
    return sorted;
  }, [post]);
  return (
    <div className="w-full">
      <div className="mx-auto max-w-3xl px-4 py-6">
        <Button variant="ghost" className="-ml-2 mb-4 gap-2" onClick={onBack}><ArrowLeft className="h-4 w-4"/> Back to Blog</Button>
        <div className={`rounded-2xl border bg-white ${liteBlue.border}`}>
          <div className={`relative h-56 w-full rounded-t-2xl bg-gradient-to-br ${grad}`}>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex h-24 w-24 items-center justify-center rounded-full bg-white/80 text-2xl font-bold text-[#0B5BD3] shadow">
                CX
              </div>
            </div>
          </div>
          <div className="p-8 space-y-8">
            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5"/>{formatDate(post.date)}</span>
              <span>•</span>
              <span className="inline-flex items-center gap-1"><User className="h-3.5 w-3.5"/>{post.author}</span>
              <span>•</span>
              <span className="inline-flex items-center gap-1"><Tag className="h-3.5 w-3.5"/>{post.tags.join(", ")}</span>
              <Button variant="ghost" size="sm" className="ml-auto gap-2" onClick={handleShare}><Share2 className="h-4 w-4"/>Share</Button>
            </div>
            <h1 className="text-3xl font-bold leading-tight">{post.title}</h1>
            {post.subtitle && <p className="text-slate-600">{post.subtitle}</p>}

            {post.sections && (
              <div className={`rounded-xl border p-5 ${liteBlue.border} ${liteBlue.bgBand}`}>
                <div className="text-xs font-semibold uppercase tracking-widest text-[#0B5BD3]">On this page</div>
                <ol className="mt-2 list-decimal pl-5 text-sm text-slate-700 space-y-1">
                  {post.sections.map((sec: any, i: number) => (
                    <li key={i}><a href={`#sec-${i}`} className="hover:underline">{sec.h2}</a></li>
                  ))}
                </ol>
              </div>
            )}

            <Separator className="my-8" />
            <article className="prose prose-slate max-w-none space-y-10">
              {post.sections
                ? post.sections.map((sec: any, i: number) => (
                    <section id={`sec-${i}`} key={i} className="scroll-mt-28 space-y-4">
                      <h2 className="text-2xl font-semibold mt-8 mb-3">{sec.h2}</h2>
                      {sec.p?.map((para: string, j: number) => (
                        <p key={j} dangerouslySetInnerHTML={{ __html: para.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
                      ))}
                      {sec.bullets && (
                        <ul className="list-disc pl-6 space-y-1">
                          {sec.bullets.map((b: string, k: number) => (
                            <li key={k} dangerouslySetInnerHTML={{ __html: b }} />
                          ))}
                        </ul>
                      )}
                      {sec.checklist && (
                        <div className={`mt-6 rounded-xl border p-5 ${liteBlue.border} ${liteBlue.bgBand}`}>
                          <div className="text-sm font-semibold mb-2">Implementation checklist</div>
                          <ul className="list-disc pl-5 space-y-1">
                            {sec.checklist.map((b: string, k: number) => (
                              <li key={k}>{b}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {sec.note && (
                        <p className="mt-2 text-xs text-slate-500">{sec.note}</p>
                      )}
                    </section>
                  ))
                : post.body?.split("\n").map((para: string, i: number) => (
                    <p
                      key={i}
                      dangerouslySetInnerHTML={{
                        __html: para
                          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                          .replace(/\- (.*?)(\n|$)/g, '• $1'),
                      }}
                    />
                  ))}
            </article>

            <Separator className="my-10 bg-gray-200" />

            <div className={`rounded-2xl border p-8 ${liteBlue.border} ${liteBlue.grad}`}>
              <div className="grid items-start gap-4 md:grid-cols-[1.5fr_.5fr]">
                <div className="min-w-0 space-y-2">
                  <div className="text-sm font-semibold">{cta.title}</div>
                  <p className="text-slate-700 break-words">{cta.body}</p>
                </div>
                <div className="flex gap-3 md:justify-end">
                  <Button className="bg-black text-white hover:bg-black/90" onClick={() => navigation.openDemoModal()}>Schedule a Demo</Button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-10">
          <div className="mb-3 text-sm font-semibold">Related posts</div>
          <div className="grid gap-4 md:grid-cols-3">
            {relatedPosts.map((rp) => (
              <Card
                key={rp.id}
                className={`overflow-hidden rounded-2xl ${liteBlue.border} border cursor-pointer hover:shadow-md transition-shadow`}
                onClick={() => onOpenPost(rp)}
              >
                <div className={`relative h-24 ${liteBlue.grad}`}>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/80 text-lg font-bold text-[#0B5BD3] shadow">
                      CX
                    </div>
                  </div>
                </div>
                <CardContent className="p-4">
                  <div className="text-sm font-semibold leading-snug line-clamp-2">{rp.title}</div>
                  <div className="mt-2 text-xs text-slate-500">Read <ChevronRight className="inline h-3.5 w-3.5"/></div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ListingPage({ openPost }: { openPost: (p: any)=>void }) {
  const navigation = useNavigationStore();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [active, setActive] = useState("All");
  const [sort, setSort] = useState("latest");

  const categories = ["All", "ReferralCX", "SchedulingCX", "EceptionistCX", "ProbationCX", "CaseCX", "CellCX", "VaxManagerCX", "AI", "Operational AI", "EHR Strategy"];

  const featuredPost = POSTS.find(p => p.featured === true);

  const posts = useMemo(() => {
    let arr = POSTS.filter(
      (p) => !p.featured && // Exclude featured post from grid
        (active === "All" || p.tags.includes(active)) &&
        (query === "" || (p.title + p.excerpt + p.tags.join(" ")).toLowerCase().includes(query.toLowerCase()))
    );
    if (sort === "latest") arr = arr.sort((a, b) => new Date(b.date as any as string).getTime() - new Date(a.date as any as string).getTime());
    if (sort === "oldest") arr = arr.sort((a, b) => new Date(a.date as any as string).getTime() - new Date(b.date as any as string).getTime());
    if (sort === "read") arr = arr.sort((a, b) => (a.read || 0) - (b.read || 0));
    return arr;
  }, [query, active, sort]);

  return (
    <>
      <Hero onSearch={setQuery} />
      {featuredPost && <FeaturedHero post={featuredPost} onOpen={openPost} />}

      <div className={`${liteBlue.bgBand} py-4`}>
        <div className="mx-auto max-w-6xl px-4">
          <div className="text-sm font-semibold text-slate-700">Latest articles</div>
        </div>
        <Filters categories={categories} active={active} setActive={setActive} sort={sort} setSort={setSort} />
      </div>

      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 px-4 pb-16 pt-6 md:grid-cols-[1fr_320px]">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {posts.map((p) => (
            <BlogCard key={p.id} post={p} onOpen={openPost} />
          ))}
        </div>
        <aside className="hidden md:block">
          <Sidebar />
        </aside>
        </div>
        
      <div className={`border-t ${liteBlue.grad}`}>
        <div className="mx-auto max-w-6xl px-4 py-12">
          <div className="grid items-center gap-4 md:grid-cols-[1.5fr_.5fr]">
            <div>
              <h3 className="text-xl font-semibold">Ready to modernize your operations?</h3>
              <p className="text-slate-700">See how EceptionistCX unifies referrals, scheduling, and complex workflows.</p>
            </div>
            <div className="flex gap-3 md:justify-end">
              <Button className="bg-black text-white hover:bg-black/90" onClick={() => navigation.openDemoModal()}>Schedule a Demo</Button>
              <Button variant="outline" onClick={() => navigate('/whitepapers')}>Explore White Papers</Button>
            </div>
        </div>
      </div>
    </div>
    </>
  );
}

export default function Blog() {
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [activePost, setActivePost] = useState<any | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { slug } = useParams();

  // Handle URL-based navigation
  useEffect(() => {
    if (slug) {
      // Find post by slug
      const post = POSTS.find(p => p.id === slug);
      if (post) {
        setActivePost(post);
        setIsDetailOpen(true);
        if (typeof window !== "undefined") {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      } else {
        // Post not found, redirect to blog listing
        navigate('/blog', { replace: true });
      }
    } else if (location.pathname === "/blog") {
      // User navigated to /blog, show listing
      setIsDetailOpen(false);
      setActivePost(null);
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  }, [slug, location.pathname, navigate]);

  const openPost = (post: any) => {
    navigate(`/blog/${post.id}`);
  };

  const goHome = () => {
    navigate('/blog');
  };


  return (
    <div className="min-h-screen w-full bg-white text-slate-900">
      {!isDetailOpen ? (
        <div className="mx-auto max-w-6xl px-4 pt-6">
          <ListingPage openPost={(post) => openPost(post)} />
        </div>
      ) : (
        activePost && <DetailPage post={activePost} onBack={goHome} onOpenPost={openPost} />
      )}

    </div>
  );
}


